// Explore-stats page: First, Most and More — records hub (#226).
// A tile grid of single-record cards (youngest MP, oldest MP, longest-
// serving, fastest from election to minister, etc.), each with a top
// holder + 5 runners-up + methodology footnote. The picked records are
// the 8 that have clean methodology + complete enough data; division-
// voted-in / floor-time records can light up later once those data
// sources are routed in.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Link, Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';

function RecordCard({ card }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start',
                    justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="overline" color="text.secondary">
            {card.label}
          </Typography>
          <Tooltip title={card.methodology}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary',
                                                       opacity: 0.7 }} />
          </Tooltip>
        </Box>
        <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
          {card.phid ? (
            <Link component={RouterLink} to={`/parliamentarians/${card.phid}`}
                  underline="hover">
              {card.value}
            </Link>
          ) : card.value}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {card.headline}
        </Typography>
        {card.sub && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block' }}>
            {card.sub}
          </Typography>
        )}
        {card.runners_up && card.runners_up.length > 0 && (
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid',
                      borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 0.5,
                               fontWeight: 600 }}>
              Runners-up
            </Typography>
            <Stack spacing={0.5}>
              {card.runners_up.map((r) => (
                <Box key={(r.phid || '') + r.name}
                     sx={{ display: 'flex', gap: 1, fontSize: 12 }}>
                  <Box sx={{ flex: 1 }}>
                    {r.phid ? (
                      <Link component={RouterLink}
                            to={`/parliamentarians/${r.phid}`}
                            underline="hover">{r.name}</Link>
                    ) : r.name}
                  </Box>
                  <Box sx={{ minWidth: 70, textAlign: 'right',
                              color: 'text.secondary' }}>
                    {r.value}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function FirstMostMore() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('first-most-more');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const cards = data?.charts?.cards || [];

  return (
    <ExploreStatsPage
      title="First, Most and More"
      description="Records-book scorecard over the parliamentarians + ministry records: youngest at first election, longest-serving ever, fastest from election to first ministry, most ministerial roles, oldest and youngest currently sitting members, and more. Each card carries a methodology footnote (hover the info icon) and a 5-deep runners-up list. The picked records are the ones with clean methodology + complete enough data — divisions-voted-in / floor-time records can light up later as those sources are routed in."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="first-most-more"
    >
      {data && (
        <Box>
          <Grid container spacing={2}>
            {cards.map((card) => (
              <Grid item xs={12} md={6} lg={4} key={card.id}>
                <RecordCard card={card} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
